import axios from "axios";
import UserService from "../../services/UserService";
import {
	buildFormDataFromRecord,
	buildUpsertRow,
	getCoverageTableConfig,
	normalizeCoverageRecord,
	resolveSchemaFromRecord,
} from "./blackAndWhiteListsConfig";

const API_BASE_PATH =
	process.env.NODE_ENV === "production"
		? process.env.REACT_APP_PROD_API_BASE_PATH
		: process.env.REACT_APP_DEV_API_BASE_PATH;

const API_BASE_ORIGIN =
	process.env.NODE_ENV === "production"
		? window.location.origin
		: process.env.REACT_APP_DEV_API_BASE_URL;

const apiClient = axios.create();

const withAuth = (config = {}) =>
	new Promise((resolve, reject) => {
		if (!UserService.isLoggedIn()) {
			reject(new Error("Not authenticated."));
			return;
		}

		const applyAuth = () => {
			const token =
				UserService.getUsername() === "efesto"
					? localStorage.getItem("token")
					: UserService.getToken();

			resolve({
				...config,
				headers: {
					...config.headers,
					Authorization: `Bearer ${token}`,
				},
			});
		};

		UserService.updateToken(applyAuth);
	});

const request = async (method, url, data) => {
	const config = await withAuth({
		method,
		url,
		...(data !== undefined ? { data } : {}),
	});

	try {
		return await apiClient.request(config);
	} catch (error) {
		const message =
			error?.response?.data?.message ||
			error?.response?.data?.error ||
			error?.message ||
			"Request failed.";
		throw new Error(message);
	}
};

const parseResponseData = (value) => {
	if (value == null) return null;
	if (typeof value === "string") {
		try {
			return JSON.parse(value);
		} catch {
			return value;
		}
	}
	return value;
};

const extractResponseData = (response) =>
	parseResponseData(
		response?.data?.data !== undefined ? response.data.data : response?.data
	);

const normalizeRows = (data) => {
	const parsed = parseResponseData(data);
	if (parsed == null) return [];
	return Array.isArray(parsed) ? parsed : [parsed];
};

export const buildCoverageApiUrl = (tableKey, { id, upsert = false } = {}) => {
	const tableConfig = getCoverageTableConfig(tableKey);
	if (!tableConfig) return null;

	const segment = upsert
		? `${tableConfig.apiSegment}_upsert`
		: tableConfig.apiSegment;

	const url = new URL(
		`${API_BASE_PATH}/ftth_coverage/${segment}`,
		API_BASE_ORIGIN
	);

	if (id) {
		url.searchParams.set("id", id);
	}

	return url;
};

export const fetchCoverageRecord = async (tableKey, id) => {
	const tableConfig = getCoverageTableConfig(tableKey);
	if (!tableConfig) {
		throw new Error("Unknown table.");
	}

	if (!id) {
		throw new Error("Record ID is required.");
	}

	const url = buildCoverageApiUrl(tableKey, { id });
	if (!url) throw new Error("Invalid API URL.");

	const response = await request("get", url.href);
	const rows = normalizeRows(extractResponseData(response));

	if (!rows.length) {
		throw new Error("Record not found.");
	}

	const record = normalizeCoverageRecord(rows[0], tableConfig);

	return {
		record,
		schema: resolveSchemaFromRecord(record, tableConfig, true),
		formData: buildFormDataFromRecord(record, tableConfig, { addMode: false }),
	};
};

export const saveCoverageRecord = async (tableKey, merged, { editMode = false } = {}) => {
	const tableConfig = getCoverageTableConfig(tableKey);
	if (!tableConfig) {
		throw new Error("Unknown table.");
	}

	const url = buildCoverageApiUrl(tableKey, { upsert: true });
	if (!url) throw new Error("Invalid API URL.");

	const row = buildUpsertRow(tableKey, merged, { editMode });
	if (!row) throw new Error("Invalid save payload.");

	const response = await request("post", url.href, [row]);
	return extractResponseData(response);
};

export const deleteCoverageRecord = async (tableKey, id) => {
	const tableConfig = getCoverageTableConfig(tableKey);
	if (!tableConfig) {
		throw new Error("Unknown table.");
	}

	if (!id) {
		throw new Error("Record ID is required.");
	}

	const url = buildCoverageApiUrl(tableKey);
	if (!url) throw new Error("Invalid API URL.");

	const response = await request("delete", url.href, { ids: [id] });
	return extractResponseData(response);
};

export { createEmptyRecord } from "./blackAndWhiteListsConfig";
